import { MouseEvent, ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { AddIcon, ChevronDownIcon } from '../../icons';
import { tokens } from '../../theme/tokens';
import { ACCOUNT_MENU_POPOVER_PROPS, ACCOUNT_MENU_WIDTH, AccountMenuItem } from '../AccountMenu';
import { ContextMenu, ContextMenuContext } from '../ContextMenu';
import { Popover } from '../Popover';
import { PRESSED_INSET } from './ButtonTypes';

export type SplitButtonItem = {
	title: string;
	icon?: ReactNode;
	link?: string;
	onClick?: () => void;
};

const Container = styled.div<{ $disabled?: boolean }>`
	display: inline-flex;
	align-items: stretch;
	height: 33px;
	box-sizing: border-box;
	overflow: hidden;
	border-radius: ${tokens.radius.full}px;
	background: ${tokens.surface.white};
	border: 1px solid ${tokens.border.default};
	box-shadow: ${tokens.shadow.button};
	color: ${tokens.text.primary};
	font-family: ${tokens.typography.fontFamily};
	font-weight: ${tokens.typography.weights.medium};
	font-size: ${tokens.typography.sizes.label}px;
	line-height: 1;

	${(props) =>
		props.$disabled &&
		css`
			color: ${tokens.text.disabled};
			border-color: ${tokens.border.subtle};
			pointer-events: none;
		`}
`;

const segment = css`
	display: flex;
	align-items: center;
	height: 100%;
	border: none;
	background: transparent;
	color: inherit;
	font: inherit;
	text-decoration: none;
	cursor: pointer;
	box-sizing: border-box;

	svg {
		display: block;
		flex-shrink: 0;
		width: 16px;
		height: 16px;
		color: inherit;
	}

	&:hover,
	&:focus-visible {
		background: ${tokens.surface.primary};
		outline: none;
	}

	&:active {
		background: ${tokens.surface.secondary};
		box-shadow: ${PRESSED_INSET};
	}
`;

const Action = styled.button`
	${segment}
	gap: ${tokens.gap.inline}px;
	padding: 0 ${tokens.gap.element}px 0 ${tokens.padding.control}px;
	white-space: nowrap;
`;

const ActionLink = styled(Link)`
	${segment}
	gap: ${tokens.gap.inline}px;
	padding: 0 ${tokens.gap.element}px 0 ${tokens.padding.control}px;
	white-space: nowrap;
`;

const Divider = styled.div`
	width: 1px;
	flex-shrink: 0;
	align-self: stretch;
	background: ${tokens.border.input};
`;

const Toggle = styled.button<{ $open?: boolean }>`
	${segment}
	padding: 0 ${tokens.padding.compact}px 0 ${tokens.gap.inline}px;

	${(props) =>
		props.$open &&
		css`
			background: ${tokens.surface.secondary};
			box-shadow: ${PRESSED_INSET};
		`}
`;

const ItemLink = styled(Link)`
	display: block;
	color: inherit;
	text-decoration: none;
`;

export function SplitButton(props: {
	title?: ReactNode;
	icon?: ReactNode;
	link?: string;
	onClick?: (event: MouseEvent) => void;
	items: SplitButtonItem[];
	disabled?: boolean;
	toggleLabel?: string;
}) {
	const [open, setOpen] = useState(false);
	const items = props.items.filter((item) => item.title);
	const hasMenu = items.length > 0;
	const icon = props.icon ?? <AddIcon size={16} />;

	const fallback = props.title == null ? items[0] : undefined;

	const title = props.title ?? fallback?.title;
	const link = props.link ?? fallback?.link;
	const onClick = props.onClick ?? fallback?.onClick;

	const close = () => setOpen(false);

	const handleActionClick = (event: MouseEvent) => {
		if (props.disabled) {
			return;
		}

		if (onClick) {
			onClick(event);
			return;
		}

		if (!link && hasMenu) {
			event.preventDefault();
			setOpen(true);
		}
	};

	const action =
		link && !props.disabled ? (
			<ActionLink to={link} onClick={handleActionClick}>
				{icon}
				{title}
			</ActionLink>
		) : (
			<Action type="button" onClick={handleActionClick} disabled={props.disabled}>
				{icon}
				{title}
			</Action>
		);

	const menu = (
		<ContextMenuContext.Provider value={{ dismiss: close }}>
			<ContextMenu minWidth={ACCOUNT_MENU_WIDTH}>
				{items.map((item) =>
					item.link ? (
						<ItemLink key={item.title} to={item.link}>
							<AccountMenuItem icon={item.icon} title={item.title} />
						</ItemLink>
					) : (
						<AccountMenuItem
							key={item.title}
							icon={item.icon}
							title={item.title}
							onClick={item.onClick}
						/>
					),
				)}
			</ContextMenu>
		</ContextMenuContext.Provider>
	);

	return (
		<Popover.Target
			visible={open}
			onDismiss={close}
			contents={menu}
			elevation={ACCOUNT_MENU_POPOVER_PROPS.elevation}
			offset={ACCOUNT_MENU_POPOVER_PROPS.offset}
			style={{ display: 'inline-flex' }}
		>
			<Container $disabled={props.disabled}>
				{action}
				{hasMenu && (
					<>
						<Divider />
						<Toggle
							type="button"
							$open={open}
							aria-haspopup="menu"
							aria-expanded={open}
							aria-label={props.toggleLabel}
							disabled={props.disabled}
							onClick={(event) => {
								event.preventDefault();
								event.stopPropagation();
								setOpen((visible) => !visible);
							}}
						>
							<ChevronDownIcon size={16} />
						</Toggle>
					</>
				)}
			</Container>
		</Popover.Target>
	);
}
